/**
 * How far back a metric tile queries.
 *
 * FURTHER THAN THE DASHBOARD ASKS, deliberately. A metric shows one period and
 * a history, and the history is what makes the single number readable — but a
 * dashboard set to "Current month" has a window one month wide, which is a
 * sparkline of one point and a comparison against nothing.
 *
 * So the tile widens its own window backwards. The dashboard's window still
 * decides WHICH period is reported (see `selectMetricPeriods`); it just stops
 * deciding how much context is fetched to report it against.
 *
 * The widened window ends where the dashboard's does, or today — whichever is
 * earlier. A dashboard set to "Current year" runs to 31 December, and querying
 * to the end of a year that has not happened buys empty buckets on the right
 * of every sparkline.
 */
import type { ComparisonWindows, Grain, ReportSpec } from '@clockwise/semantic-core';
import { comparisonWindows, grainEnd, grainOfDimension, showAsFor } from '@clockwise/semantic-core';

/** How many periods of history a metric tile fetches. */
export const TREND_PERIODS = 12;

const iso = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * The reader's calendar day, LOCAL — the same basis `resolveDateRange` used
 * for the window it is compared with. Read in UTC, a US evening is already
 * tomorrow and an Indian early morning still yesterday, so near midnight a
 * running window looked finished or a month that had begun looked future.
 */
const localDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * The window a metric tile should query: `periods` grains ending at the
 * dashboard's window end, or today, whichever comes first.
 */
export function metricWindow(input: {
  from: string;
  to: string;
  grain: Grain;
  periods?: number;
  today?: Date;
}): { from: string; to: string } {
  const { from, to, grain } = input;
  const periods = input.periods ?? TREND_PERIODS;
  const todayIso = localDay(input.today ?? new Date());

  // A WINDOW ENTIRELY AHEAD OF NOW is left exactly as asked. Clamping its end
  // to today would put the end before the start, the widening below would then
  // pull the start back a year, and the tile would answer with the last twelve
  // periods of history while the filter bar above it said next quarter —
  // a confident number for a period nobody asked about. Handed back unchanged,
  // it returns no rows and the tile renders its ordinary empty state.
  if (from > todayIso) return { from, to };

  // Otherwise never past today: an unfinished period is worth showing, a
  // period that has not started is a gap on the right of the chart.
  const end = to < todayIso ? to : todayIso;
  const endDate = new Date(`${end}T00:00:00Z`);

  const start = new Date(endDate);
  if (grain === 'month') {
    start.setUTCDate(1);
    start.setUTCMonth(start.getUTCMonth() - (periods - 1));
  } else if (grain === 'week') {
    // Back to the Monday of the week `periods - 1` weeks ago.
    const isoDow = (start.getUTCDay() + 6) % 7;
    start.setUTCDate(start.getUTCDate() - isoDow - (periods - 1) * 7);
  } else {
    start.setUTCDate(start.getUTCDate() - (periods - 1));
  }

  // NEVER NARROWER THAN THE DASHBOARD ASKED — nor than its MIRROR. Widening is
  // context; narrowing would silently answer a different question than the one
  // on screen — a dashboard set to "Previous year" must not come back showing
  // 12 months ending today. And the sparkline shows the period AND the period
  // it is compared against, so "This year" reaches back to last January even
  // though twelve months would stop short of it.
  const mirrorFrom = comparisonWindows({ from, to, today: todayIso }).previous.from;
  return { from: [iso(start), from, mirrorFrom].sort()[0] as string, to: end };
}

/**
 * The two questions behind a metric's figure and its comparison: the measure
 * over the reader's window, and over its mirror (see `comparisonWindows`).
 *
 * ASKED WHOLE, not added up from the series. A quarter's utilization is not
 * the sum of three monthly percentages — nor their average, when the months
 * have different capacity — so the figure for a window longer than one grain
 * has to come from a query over that window. Built from the tile's own
 * effective spec, so the filters and scope are the series' exactly; only the
 * window differs, and the grouping is dropped.
 *
 * `null` for anything that is not a trended metric: an untrended one already
 * totals the reader's window, and there is no series to mirror. `null` too for
 * a measure shown as a SHARE: ungrouped, every share is the whole (100%), and
 * `pctCol` without a row dimension is refused outright — such a metric keeps
 * reporting one grain of its series.
 *
 * `fromSeries` says the two numbers need no query of their own: for ONE WHOLE
 * MONTH over a monthly series, the window and its mirror (the whole previous
 * month) are two buckets of the series the tile already fetched.
 */
export function metricComparisonSpecs(
  spec: ReportSpec,
  selected: { from: string; to: string },
  today: Date = new Date(),
): MetricComparisonSpecs | null {
  const measureId = spec.measures[0];
  if (!isTrendedMetric(spec) || !measureId || showAsFor(spec, measureId) !== 'raw') return null;
  const windows = comparisonWindows({ ...selected, today: localDay(today) });
  const fromSeries =
    grainOfDimension(spec.rows[0] ?? '') === 'month' &&
    selected.from.endsWith('-01') &&
    grainEnd(selected.from.slice(0, 7), 'month') === selected.to;
  const total = (window: { from: string; to: string }): ReportSpec => ({
    ...spec,
    // Concrete dates, no macro: the mirror has no preset that names it, and a
    // macro would be re-resolved back to the reader's window.
    period: undefined,
    from: window.from,
    to: window.to,
    rows: [],
    cols: [],
    rowSort: [],
    colSort: [],
    subtotals: [],
    suppressRepeats: [],
    collapsed: undefined,
    toggledGroups: undefined,
    totals: { row: false, col: false },
    limit: undefined,
    offset: undefined,
  });
  return {
    current: total(windows.current),
    previous: total(windows.previous),
    windows,
    fromSeries,
  };
}

export interface MetricComparisonSpecs {
  /** The measure over the reader's window — also what the figure drills into. */
  current: ReportSpec;
  /** The measure over its mirror. */
  previous: ReportSpec;
  windows: ComparisonWindows;
  /** Both numbers are buckets of the series: ask neither question. */
  fromSeries: boolean;
}

/** Is this spec a metric with a history — one measure over one time grain? */
export function isTrendedMetric(spec: Pick<ReportSpec, 'viz' | 'rows'>): boolean {
  return spec.viz === 'metric' && spec.rows.length === 1;
}

// Re-exported so the callers here have one import. The DEFINITION lives in
// `semantic-core`, beside the rule that reads the series, because the window
// and the series disagreeing about what a period is was exactly the bug.
export { grainOfDimension };
